import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Loader2, FileText, Download } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CGUModal = ({ 
  isOpen, 
  onClose, 
  onAccept, 
  showAcceptButton = true,
  clubId = null 
}) => {
  const [cgu, setCgu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchCGU();
    }
  }, [isOpen, clubId]);

  const fetchCGU = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = clubId ? { club_id: clubId } : {};
      const response = await axios.get(`${API_URL}/api/cgu`, { params });
      setCgu(response.data);
    } catch (err) {
      console.error('Error fetching CGU:', err);
      setError('Impossible de charger les CGU');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (onAccept && cgu) {
      onAccept(cgu.version);
    }
  };

  const handleDownloadPDF = () => {
    // Create a printable version
    const printWindow = window.open('', '_blank');
    if (printWindow && cgu) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Conditions Générales d'Utilisation</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
            h1 { color: #2E4A3D; border-bottom: 2px solid #2E4A3D; padding-bottom: 10px; }
            h2 { color: #2E4A3D; margin-top: 30px; }
            h3 { color: #444; }
            hr { margin: 20px 0; border: none; border-top: 1px solid #ddd; }
            .version { color: #666; font-size: 12px; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="version">Version ${cgu.version} - ${new Date(cgu.updated_at).toLocaleDateString('fr-FR')}</div>
          ${cgu.content.replace(/^#/gm, '<h1>').replace(/^##/gm, '<h2>').replace(/^###/gm, '<h3>').replace(/\n/g, '<br/>')}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2E4A3D]">
            <FileText className="w-5 h-5" />
            Conditions Générales d'Utilisation
          </DialogTitle>
          {cgu && (
            <p className="text-sm text-stone-500">
              Version {cgu.version} • Mise à jour le {new Date(cgu.updated_at).toLocaleDateString('fr-FR')}
              {cgu.is_default && <span className="ml-2 text-amber-600">(Modèle par défaut)</span>}
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E4A3D]" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              {error}
            </div>
          ) : cgu ? (
            <ScrollArea className="h-[50vh] pr-4">
              <div className="prose prose-sm max-w-none prose-headings:text-[#2E4A3D] prose-h1:text-xl prose-h2:text-lg prose-h3:text-base">
                <ReactMarkdown>{cgu.content}</ReactMarkdown>
              </div>
            </ScrollArea>
          ) : null}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            disabled={loading || !cgu}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Imprimer / PDF
          </Button>
          
          {showAcceptButton ? (
            <>
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button 
                onClick={handleAccept}
                disabled={loading || !cgu}
                className="btn-primary"
                data-testid="cgu-accept-btn"
              >
                J'accepte les CGU
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CGUModal;
